/* eslint-disable max-lines-per-function */
import { Autocomplete, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import './mapSkills.scss';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { CONFIRMATION, NO, SKILL_MAP_DASHBOARD, YES } from '../../../Constants/strings';
import { makeStyles } from '@mui/styles';
import ProfilePicPlaceHolder from '../../../Assets/images/png/profil-pic-placeholder-large.png';
import CommonTextField from '../../../Components/TextField/textField';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import {
  fetchVertivalList,
  fetchEmployeeInfo,
  handleBeforeUnload,
  fetchDepartmentList,
  getSkillVertDeptMap,
  handleClickOutside,
} from '../apiCallUtil';
import { logError } from '../../../Utils/logger';
import NoData from '../Dashboard/noData';
import SkillVerticalList from './SkillVerticalList/skillVerticalList';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import FormDialog from '../../../Components/Modal/modal';

const useNoOptionsStyles = (noEmployeeData) =>
  makeStyles({
    noOptions: {
      display: noEmployeeData ? 'block' : 'none',
    },
  });

function MapSkills() {
  const navigate = useNavigate();
  const [selectedEmployee, setSelectedEmployee] = useState({});
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [employeeIDValue, setEmployeeIDValue] = useState('');
  const [verticalOption, setVerticalOption] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [departmentOption, setDepartmentOption] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [noDataMessage, setNoDataMessage] = useState('');
  const [skillVerticalList, setSkillVerticalList] = useState([]);
  const [loadingEmployeeList, setLoadingEmployeeList] = useState(false);
  const [employeeAutocompleteError, setEmployeeAutocompleteError]
    = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [popState, setPopState] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noEmployeeData, setNoEmployeeData] = useState(false);
  const useStyles = useNoOptionsStyles(noEmployeeData);
  const styles = useStyles();

  useEffect(() => {
    // Handle beforeunload event when reloading or closing the tab
    const handleBeforeUnloadHandler = (event) =>
      handleBeforeUnload(event, isDirty);
    window.addEventListener('beforeunload', handleBeforeUnloadHandler);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnloadHandler);
  }, [isDirty]);

  useEffect(() => {
    // Handle popstate event when navigating back
    if (isDirty) {
      const handlePopstate = () => {
        setPopState(true);
        setIsModalOpen(true);
      };
      history.pushState(null, null, window.location.pathname);
      window.addEventListener('popstate', handlePopstate);
      return () => {
        window.removeEventListener('popstate', handlePopstate);
      };
    }
  }, [isDirty, popState]);

  useEffect(() => {
    const handleClickOutsideHandler = (event) => {
      const confirmNavigation = handleClickOutside(event, isDirty, setIsModalOpen);
      if (!confirmNavigation) {
        // Prevent default action if user cancels navigation
        event.preventDefault();
      }
    };

    document.addEventListener('mousedown', handleClickOutsideHandler); // Add event listener when component mounts

    return () => {
      document.removeEventListener('mousedown',
        handleClickOutsideHandler); // Clean up event listener when component unmounts
    };
  }, [isDirty]);

  useEffect(() => {
    const handleClickOutsideHandler = (
      event, // Function to handle clicks outside the component
    ) =>
      handleClickOutside(
        event,
        isDirty,
        setIsModalOpen,
      );
    document.addEventListener('mousedown', handleClickOutsideHandler); // Add event listener when component mounts

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideHandler); // Clean up event listener when component unmounts
    };
  }, [isDirty]);

  useEffect(() => {
    fetchVertivalList().then(setVerticalOption);
    fetchDepartmentList().then(setDepartmentOption);
  }, []);

  useEffect(() => {
    if (selectedDepartment?.id && selectedVertical?.id) {
      getSkillVerticalMap(selectedVertical.id, selectedDepartment.id);
    }
  }, [selectedDepartment, selectedVertical]);

  const handleEmployeeSelection = async (event, newInputValue) => {
    setNoEmployeeData(false);
    if (newInputValue.length >= 3) {
      setLoadingEmployeeList(true);
      debouncedFetchEmployeeInfo(newInputValue);
    } else {
      setLoadingEmployeeList(false);
      setEmployeeSuggestions([]);
      setNoEmployeeData(false);
    }
  };

  const fetchEmployeeData = async (inputValue) => {
    try {
      const employeeInfo = await fetchEmployeeInfo(inputValue);
      setLoadingEmployeeList(false);
      setNoEmployeeData(employeeInfo.length === 0);
      setEmployeeSuggestions(employeeInfo);
      setEmployeeAutocompleteError('');
    } catch (error) {
      logError('Error fetching user info:', error);
      setEmployeeSuggestions([]);
      setNoEmployeeData(false);
    }
  };

  const debouncedFetchEmployeeInfo = useCallback(
    debounce((inputValue) => fetchEmployeeData(inputValue), 1000),
    [],
  );

  const handleSelectedEmployeeError = async () => {
    setEmployeeAutocompleteError('Please fill the required fields');
  };

  const handleEmpIDChange = (event) => {
    setEmployeeIDValue(event.target.value);
  };

  const handleAutocompleteChange = (event, newValue) => {
    const matchedVertical = verticalOption.find(option => option.name === newValue?.vertical);
    const matchedDepartment = departmentOption.find(option => option.name === newValue?.department);
    if (matchedVertical) {
      handleVerticalSelection({ target: { value: matchedVertical } });
    } else {
      setSelectedVertical(null);
      setSkillVerticalList([]);
      setNoDataMessage('');
    }

    if (matchedDepartment) {
      handleDepartmentSelection({ target: { value: matchedDepartment } });
    } else {
      setSelectedDepartment(null);
      setSkillVerticalList([]);
      setNoDataMessage('');
    }

    setEmployeeIDValue(newValue ? newValue.employeeId : '');
    setSelectedEmployee(newValue ? { ...newValue } : null);
  };

  const getSkillVerticalMap = async (verticalId, departmentId) => {
    try {
      const result = await getSkillVertDeptMap(verticalId, departmentId);
      if (result.skillDetails) {
        setSkillVerticalList(result.skillDetails);
        setNoDataMessage('');
      } else if (result.failure) {
        setSkillVerticalList([]);
        setNoDataMessage(result.failure);
      }
    } catch (error) {
      logError('Error fetching skill vertical maps:', error);
    }
  };

  const handleVerticalSelection = (event) => {
    setSelectedVertical(event.target.value);
  };

  const handleDepartmentSelection = (event) => {
    setSelectedDepartment(event.target.value);
  };

  const handleBackButtonClick = () => {
    if (isDirty) {
      setIsModalOpen(true);
    } else {
      navigate('/skills');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setPopState(false);
  };

  const handleModalConfirm = () => {
    setIsModalOpen(false);
    navigate('/skills');
  };

  return (
    <div className="map-skill-ui-outer-layer">
      <div className="map-skill-ui">
        <div className="map-skill-title">
          <IconButton
            className="map-skill-back-icon"
            onClick={handleBackButtonClick}
          >
            <ArrowBackIosIcon />
          </IconButton>
          <Typography className="map-skill-title-text">
            {SKILL_MAP_DASHBOARD.MAP_SKILL.TITLE_TEXT}
          </Typography>
        </div>

        <div className="map-skill-search-container">
          <div className="map-skill-search-item employee-name-div">
            <div className="map-skill-input-container">
              <label className="suggested-employee-name-label">
                {
                  SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER.EMPLOYEE_NAME
                    .LABEL
                }
              </label>
              <Autocomplete
                classes={{ noOptions: styles.noOptions }}
                value={selectedEmployee}
                inputValue={selectedEmployee?.fullName}
                onInputChange={handleEmployeeSelection}
                onChange={(event, newValue) => {
                  handleAutocompleteChange(event, newValue);
                }}
                loading={loadingEmployeeList}
                loadingText={
                  <div className="map-skill-employee-loading">
                    <CircularProgress size={24} />
                  </div>
                }
                options={employeeSuggestions}
                noOptionsText={SKILL_MAP_DASHBOARD.MAP_SKILL.NO_USERNAME_MATCH}
                isOptionEqualToValue={(option, value) =>
                  option.fullName === value.fullName
                }
                getOptionLabel={(option) => option.fullName || ''}
                renderOption={(props, option) => {
                  const imageUrl
                    = option?.photoSignedUrl || ProfilePicPlaceHolder;
                  return (
                    <li {...props}>
                      <div className="employee-suggestion-wrapper">
                        <img
                          src={imageUrl}
                          alt="Profile"
                          className="employee-suggestion-image"
                          onError={(event) => {
                            event.target.onerror = null; // Prevents infinite loop if placeholder image also fails
                            event.target.src = ProfilePicPlaceHolder;
                          }}
                        />
                      </div>
                      {`${option.fullName} . ${option.employeeId}`}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER
                        .EMPLOYEE_NAME.PLACEHOLDER
                    }
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '2.125rem',
                        padding: '0 0.875rem',
                      },
                      '& .MuiInputBase-input': {
                        fontSize: 'small',
                        '&::placeholder': {
                          color: 'grey',
                          fontSize: 'small',
                        },
                      },
                    }}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                  />
                )}
              />
              {employeeAutocompleteError && (
                <div className="no-employee-id-error">
                  <small>{employeeAutocompleteError}</small>
                </div>
              )}
            </div>
          </div>

          <div className="map-skill-search-item employee-id-div">
            <CommonTextField
              label={
                SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER.EMPLOYEE_ID.LABEL
              }
              textCss="employee-id"
              value={employeeIDValue}
              onChange={handleEmpIDChange}
              autoFocus={true}
              size="small"
              id="employee-name-text"
              InputProps={{
                style: { backgroundColor: '#F1F5F8' },
              }}
              disabled={true}
            />
          </div>

          <div className="map-skill-search-item vertical-div">
            <SearchDropdown
              textCss="employee-id"
              label={
                SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER.VERTICAL.LABEL
              }
              disabled={employeeIDValue === ''}
              options={verticalOption}
              value={selectedVertical}
              placeholder={
                SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER.VERTICAL
                  .PLACEHOLDER
              }
              onChange={handleVerticalSelection}
              id="skill-map-vertical-dropdown"
            />
          </div>

          <div className="map-skill-search-item department-div">
            <SearchDropdown
              textCss="employee-id"
              label={
                SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER.DEPARTMENT.LABEL
              }
              disabled={employeeIDValue === ''}
              options={departmentOption}
              value={selectedDepartment}
              placeholder={
                SKILL_MAP_DASHBOARD.MAP_SKILL.SEARCH_CONTAINER.DEPARTMENT
                  .PLACEHOLDER
              }
              onChange={handleDepartmentSelection}
              id="skill-map-department-dropdown"
            />
          </div>
        </div>

        {skillVerticalList.length === 0 && (
          <NoData
            message={
              noDataMessage || SKILL_MAP_DASHBOARD.MAP_SKILL.NO_DATA_TEXT
            }
            displayImage={!noDataMessage}
          />
        )}

        {skillVerticalList.length > 0 && (
          <SkillVerticalList
            list={skillVerticalList}
            employeeDetails={selectedEmployee}
            handleSelectedEmployeeError={handleSelectedEmployeeError}
            setIsDirty={setIsDirty}
          />
        )}
      </div>

      <FormDialog
        title={CONFIRMATION}
        open={isModalOpen}
        handleClose={handleModalClose}
        button1Action={handleModalClose}
        button2Action={handleModalConfirm}
        dialogPaperCss="target-dialog-paper"
        dialogCss="target-dialog"
        dialogBodycontent={
          <div className="target-dialog-text">
            <span>{SKILL_MAP_DASHBOARD.MAP_SKILL.EXIT_CONFIRM}</span>
          </div>
        }
        button1={NO}
        button2={YES}
        cancelButtonId="target-cancel-btn"
        submitButtonId="target-submit-btn"
      />
    </div>
  );
}

export default MapSkills;
